import React from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface ExpandedRowField {
  key: string
  label: string
  value: string | React.ReactNode
}

interface ExpandedRowDetailsProps<T = unknown, TAdditionalProps = unknown> {
  item: T
  ctx?: unknown
  visibleColumns?: string[]
  additionalProps?: TAdditionalProps
}

export const createExpandedRowComponent = <T, TAdditionalProps = unknown>(
  getFields: (item: T, t: (key: string) => string, additionalProps?: TAdditionalProps) => ExpandedRowField[]
) => {
  return ({ item, visibleColumns = [], additionalProps }: ExpandedRowDetailsProps<T, TAdditionalProps>) => {
    const { t } = useTranslation()
    
    const allFields = getFields(item, t, additionalProps)
    const hiddenFields = allFields.filter(field => !visibleColumns.includes(field.key))
    
    if (hiddenFields.length === 0) return null
    
    return (
      <Box sx={{ 
        p: 3,
        bgcolor: '#FFFFFF',
        border: '1px solid #EFEFEF',
        borderRadius: '24px'
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          {hiddenFields.map((field) => (
            <Box
              key={field.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0
              }}
            >
              <Box component="span" sx={{ 
                fontWeight: 500,
                color: '#464646',
                fontSize: 12,
                display: 'block',
                flex: '0 0 auto',
                maxWidth: '45%'
              }}>
                {field.label}
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: 8, borderTop: '1px solid #F7F7F7' }} />
              <Box 
                component="span" 
                sx={{ 
                  color: '#656565',
                  fontSize: 12,
                  fontWeight: 400,
                  display: 'block',
                  flex: '0 1 auto',
                  maxWidth: '55%',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'right',
                  '& *': {
                    color: '#656565',
                    fontSize: 12,
                    fontWeight: 400
                  }
                }}
              >
                {field.value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    )
  }
}
